/**
 * Utility for handling image URLs through proxy to solve CORS issues
 */

// Cache for converted data URLs to avoid repeated requests
const imageCache = new Map<string, string>();

/**
 * Clear the image cache (useful for testing)
 */
export function clearImageCache(): void {
  imageCache.clear();
}

/**
 * Generate proxied image URL to avoid CORS issues
 * For development, we'll try the proxy first, then fallback to original
 * @param originalUrl - The original image URL from external service
 * @returns Proxied URL that can be loaded without CORS issues
 */
export function getProxiedImageUrl(originalUrl: string | null | undefined): string {
  if (!originalUrl || typeof originalUrl !== 'string') {
    return '';
  }
  
  // Check if URL is from external domains that need proxying
  const needsProxy = isExternalImageUrl(originalUrl);
  
  if (!needsProxy) {
    return originalUrl;
  }
  
  // Return proxied URL for external domains
  return `/api/image-proxy?url=${encodeURIComponent(originalUrl)}`;
}

/**
 * Convert external image to data URL to bypass CORS (client-side solution)
 * This is a fallback when server-side proxy is not available
 * @param originalUrl - The original image URL
 * @returns Promise that resolves to data URL or original URL on error
 */
export async function convertToDataUrl(originalUrl: string): Promise<string> {
  if (!originalUrl || !isExternalImageUrl(originalUrl)) {
    return originalUrl;
  }
  
  // Check cache first
  if (imageCache.has(originalUrl)) {
    return imageCache.get(originalUrl)!;
  }
  
  try {
    // Try to fetch through our proxy first
    let response: Response;
    try {
      response = await fetch(`/api/image-proxy?url=${encodeURIComponent(originalUrl)}`);
      if (!response.ok) throw new Error('Proxy failed');
    } catch {
      // Fallback: try direct fetch (might fail due to CORS but worth trying)
      response = await fetch(originalUrl, { mode: 'cors' });
    }
    
    if (!response.ok) {
      throw new Error('Failed to fetch image');
    }
    
    const blob = await response.blob();
    
    return await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const dataUrl = reader.result as string;
        imageCache.set(originalUrl, dataUrl);
        resolve(dataUrl);
      };
      reader.onerror = () => reject(new Error('Failed to convert to data URL'));
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.warn('Failed to convert image to data URL:', error);
    return originalUrl; // Return original URL as fallback
  }
}

/**
 * Check if image URL is from external service that needs CORS proxy
 * @param url - Image URL to check
 * @returns true if URL needs proxying
 */
export function isExternalImageUrl(url: string): boolean {
  try {
    const urlObj = new URL(url);
    const externalDomains = [
      's3.eu-central-003.backblazeb2.com',
      'fal.media',
      'files.fal.media'
    ];
    
    return externalDomains.includes(urlObj.hostname);
  } catch {
    return false;
  }
}

/**
 * Get original image URL (for opening in new tab)
 * @param url - Image URL (may be proxied)
 * @returns Original URL
 */
export function getOriginalImageUrl(url: string | null | undefined): string {
  if (!url || typeof url !== 'string') {
    return '';
  }
  
  // If it's a proxied URL, extract the original URL
  if (url.includes('/api/image-proxy?url=')) {
    const urlParams = new URLSearchParams(url.split('?')[1]);
    const originalUrl = urlParams.get('url');
    return originalUrl || url;
  }
  
  return url;
}

/**
 * Get thumbnail URL with CF Images transformation for R2 URLs
 * Applies Cloudflare Image Resizing to reduce bandwidth for previews
 * Note: For external URLs, returns proxied URL without resizing (proxy doesn't support it)
 * @param url - Original image URL
 * @param width - Desired width for R2 URLs (default: 100 for table thumbnails)
 * @returns URL with CF Images transformation, proxied URL, or original URL
 */
export function getThumbnailUrl(url: string | null | undefined, width: number = 100): string {
  if (!url || typeof url !== 'string') {
    return '';
  }

  try {
    const urlObj = new URL(url);

    // Only apply to content.loremax.ai R2 URLs
    if (urlObj.hostname === 'content.loremax.ai') {
      // Skip if already has CF transformation
      if (urlObj.pathname.includes('/cdn-cgi/image/')) {
        return url;
      }

      // Apply CF Images transformation
      return `${urlObj.origin}/cdn-cgi/image/width=${width},fit=cover,format=auto,quality=85${urlObj.pathname}`;
    }

    // For external URLs, use proxy without width (proxy doesn't support resizing)
    if (isExternalImageUrl(url)) {
      return `/api/image-proxy?url=${encodeURIComponent(url)}`;
    }

    return url;
  } catch {
    return url;
  }
}